import toast from "react-hot-toast";

interface ApiErrorField {
  detail: string;
}

interface ApiErrorData {
  message?: string;
}

interface ApiErrorShape {
  status?: number;
  message?: string;
  detail?: string;
  fields?: ApiErrorField[];
  data?: ApiErrorData;
  response?: {
    data?: ApiErrorShape;
    status?: number;
  };
}

function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function toApiErrorShape(error: unknown): ApiErrorShape | null {
  if (!isObjectLike(error)) {
    return null;
  }

  if (
    "response" in error &&
    isObjectLike(error.response) &&
    "data" in error.response &&
    isObjectLike(error.response.data)
  ) {
    return error.response.data as ApiErrorShape;
  }

  return error as ApiErrorShape;
}

function extractApiErrorMessage(
  error: unknown,
  fallbackMessage = "Erro inesperado"
): string {
  const normalizedError = toApiErrorShape(error);
  if (!normalizedError) {
    return fallbackMessage;
  }

  const fieldErrors =
    normalizedError.fields && normalizedError.fields.length > 0
      ? normalizedError.fields
          .map((field) => field.detail)
          .filter((detail) => detail.length > 0)
          .join(", ")
      : "";

  const detailMessage =
    normalizedError.detail ??
    normalizedError.data?.message ??
    normalizedError.message ??
    fallbackMessage;

  if (fieldErrors.length > 0) {
    return `${detailMessage}. ${fieldErrors}`;
  }

  return detailMessage;
}

function apiErrorResolver(error: unknown) {
  const normalizedError = toApiErrorShape(error);
  if (normalizedError) {
    const status = normalizedError.status ?? normalizedError.response?.status;

    // Verifica se o status é 500
    if (status === 500) {
      const errorMessage = extractApiErrorMessage(
        normalizedError,
        "Erro interno do servidor."
      );
      // Mostra a mensagem no toast
      toast.error(`Erro: ${errorMessage}`);
    } else {
      // Trata outros status de erro
      toast.error(`Erro: ${extractApiErrorMessage(normalizedError)}`);
    }
  } else {
    // Se não houver resposta da API (por exemplo, erro de rede)
    toast.error(
      "Erro na comunicação com o servidor. Tente novamente mais tarde."
    );
  }
}

export { apiErrorResolver, extractApiErrorMessage };
